import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaFlag,
  FaTrash,
  FaBan,
  FaCommentSlash,
  FaCheckDouble,
  FaImage,
  FaVideo,
  FaMicrophone,
  FaFileAlt,
  FaInbox,
  FaExternalLinkAlt,
  FaArrowRight,
  FaCalendarDay,
  FaUserSlash,
  FaExclamationTriangle,
  FaCopy,
  FaChevronLeft,
  FaChevronRight,
  FaPaperclip,
  FaUsers,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes mrFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes mrFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes mrSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes mrZoom { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
@keyframes mrPop { 0% { transform: scale(.9); opacity: 0; } 60% { transform: scale(1.02); opacity: 1; } 100% { transform: scale(1); } }
.mr-in { animation: mrFadeUp .45s cubic-bezier(.2,.7,.2,1) both; }
.mr-fade { animation: mrFade .2s ease both; }
.mr-slide { animation: mrSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.mr-zoom { animation: mrZoom .22s cubic-bezier(.2,.8,.2,1) both; }
.mr-pop { animation: mrPop .4s cubic-bezier(.2,.8,.2,1) both; }
.mr-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.mr-press:hover { transform: translateY(-1px); }
.mr-press:active { transform: scale(.96); }
.mr-card { transition: transform .2s ease, box-shadow .25s ease, border-color .2s ease; }
.mr-card:hover { transform: translateY(-3px); box-shadow: 0 16px 30px -16px rgba(15,23,42,.35); }
.mr-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
.mr-clamp3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
@media (prefers-reduced-motion: reduce) {
  .mr-in, .mr-fade, .mr-slide, .mr-zoom, .mr-pop { animation: none; }
  .mr-press:hover, .mr-press:active, .mr-card:hover { transform: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500";

/* Server folders (from util.php -> GROUP_MSG_MEDIA, PROFILE_PICTURE) */
const PROFILE_PICTURE = "https://ycnp.in/application/profile_pic/";
const MEDIA_BASES = ["https://ycnp.in/application/group/message/", "https://ycnp.in/application/"];

/* ================= helpers ================= */

const DAY = 24 * 60 * 60 * 1000;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

const profileUrl = (f) => (!f ? null : /^https?:\/\//.test(f) ? f : PROFILE_PICTURE + f);

const guessType = (name = "") => {
  const n = String(name).toLowerCase();
  if (/\.(jpe?g|png|gif|webp|bmp|heic)$/.test(n)) return "image";
  if (/\.(mp4|webm|mov|mkv|3gp)$/.test(n)) return "video";
  if (/\.(mp3|wav|ogg|m4a|aac|opus|amr)$/.test(n)) return "audio";
  return "file";
};

const normMedia = (list) =>
  (Array.isArray(list) ? list : [])
    .map((m) => {
      if (typeof m === "string") return { url: m, type: guessType(m) };
      const url = m?.url || m?.file || m?.path || m?.name || "";
      return { url, type: m?.type || guessType(url) };
    })
    .filter((m) => m.url);

const candidates = (url) =>
  /^https?:\/\//.test(url) ? [url] : MEDIA_BASES.map((b) => b + String(url).replace(/^\/+/, ""));

const personName = (name) => (name && String(name).trim() ? name : null);

/* Media that tries each server folder until one works */
const SmartMedia = ({ media, size = "thumb", onOpen }) => {
  const list = useMemo(() => candidates(media.url), [media.url]);
  const [idx, setIdx] = useState(0);
  const failed = idx >= list.length;
  const src = list[Math.min(idx, list.length - 1)];
  const next = () => setIdx((i) => i + 1);
  const box = size === "thumb" ? "w-16 h-16" : "w-full";

  if (failed) {
    return (
      <div className={`${box} rounded-lg bg-slate-100 dark:bg-slate-700 flex flex-col items-center justify-center text-[10px] text-slate-400 p-1 text-center`} style={size === "thumb" ? {} : { aspectRatio: "4/3" }}>
        <FaFileAlt className="mb-1" />
        File not found
      </div>
    );
  }
  if (media.type === "image") {
    return (
      <button type="button" onClick={() => onOpen && onOpen(src)} className={`mr-press block ${box} rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700`}>
        <img src={src} alt="" onError={next} className={`w-full ${size === "thumb" ? "h-full" : ""} object-cover`} />
      </button>
    );
  }
  if (media.type === "video") {
    return <video src={src} onError={next} controls className={`${box} rounded-lg bg-black ${size === "thumb" ? "object-cover" : ""}`} />;
  }
  if (media.type === "audio") {
    return <audio src={src} onError={next} controls className={size === "thumb" ? "w-40" : "w-full"} />;
  }
  return (
    <a href={src} target="_blank" rel="noreferrer" className="mr-press inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-100 dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-200">
      <FaFileAlt /> View File
    </a>
  );
};

const MediaIcon = ({ type }) =>
  type === "image" ? <FaImage /> : type === "video" ? <FaVideo /> : type === "audio" ? <FaMicrophone /> : <FaFileAlt />;

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="mr-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Avatar = ({ file, name, size = 36 }) => {
  const [err, setErr] = useState(false);
  const src = profileUrl(file);
  const initials =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  if (src && !err)
    return <img src={src} alt="" onError={() => setErr(true)} className="rounded-full object-cover shrink-0 bg-slate-100" style={{ width: size, height: size }} />;
  return (
    <span className="rounded-full shrink-0 flex items-center justify-center bg-slate-200 text-slate-500 font-semibold dark:bg-slate-600 dark:text-slate-200" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {name ? initials : <FaUserSlash size={size * 0.4} />}
    </span>
  );
};

/* ================= page ================= */

const GroupMessageReport = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | text | media
  const [range, setRange] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [checked, setChecked] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type, reports}
  const [working, setWorking] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE = 12;

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v ?? ""));
      const res = await fetch(`${baseUrl}/group_report.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await post("getGroupMessageReports");
      if (data && data.error === 0 && Array.isArray(data.reports)) setReports(data.reports);
      else setReports([]);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch user message reports" });
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Each action returns true when it worked
  const doAction = async (type, r) => {
    if (type === "delete_report") {
      const data = await post("deleteGroupReport", { report_id: r.report_id });
      if (data?.error === 0) return true;
      throw new Error(data?.message || "Failed");
    }
    const data = await post("groupReportAction", { user_id: r.sender_id || "", msg_id: r.msg_id || "", group_id: r.group_id || "", op: type });
    if (data?.error === 0) return true;
    throw new Error(data?.message || "Failed");
  };

  const LABELS = {
    delete_message: { title: "Delete Message", done: "Message deleted" },
    block_user: { title: "Block in group", done: "Sender blocked in the group" },
    remove_member: { title: "Remove from group", done: "Sender removed from the group" },
    delete_report: { title: "Delete Report", done: "Report deleted" },
    resolve: { title: "Delete message & close report", done: "Message deleted and report closed" },
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, reports: list } = confirm;
    setWorking(true);
    let ok = 0;
    for (const r of list) {
      try {
        if (type === "resolve") {
          await doAction("delete_message", r);
          await doAction("delete_report", r);
        } else {
          await doAction(type, r);
        }
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setChecked([]);
    if (ok) {
      toast.current?.show({
        severity: "success",
        summary: "Done",
        detail: list.length === 1 ? LABELS[type].done : `${LABELS[type].done} (${ok} of ${list.length})`,
      });
    }
    if (ok < list.length) {
      toast.current?.show({ severity: "error", summary: "Error", detail: `${list.length - ok} failed. Try again.` });
    }
    if (type === "delete_report" || type === "resolve") setSelected(null);
    fetchReports();
  };

  /* ----- derived ----- */

  const q = query.trim();

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    const now = Date.now();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    let l = reports.filter((r) => {
      const media = normMedia(r.media_files);
      if (typeFilter === "media" && !media.length) return false;
      if (typeFilter === "text" && media.length) return false;
      if (range !== "all") {
        const d = parseAny(r.date_time);
        if (!d) return false;
        if (range === "today" && d < startOfToday) return false;
        if (range === "7d" && now - d.getTime() > 7 * DAY) return false;
        if (range === "30d" && now - d.getTime() > 30 * DAY) return false;
      }
      if (!ql) return true;
      return [r.report_id, r.title, r.description, r.msg_id, r.message_text, r.sender_name, r.sender_id, r.reporter_name, r.reporter_id, r.grp_name, r.group_id]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(ql));
    });
    l = [...l].sort((a, b) => {
      const da = parseAny(a.date_time)?.getTime() || Number(a.report_id) || 0;
      const db = parseAny(b.date_time)?.getTime() || Number(b.report_id) || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });
    return l;
  }, [reports, q, typeFilter, range, sortBy]);

  useEffect(() => setPage(1), [q, typeFilter, range, sortBy]);
  useEffect(() => {
    const valid = new Set(reports.map((r) => r.report_id));
    setChecked((c) => c.filter((id) => valid.has(id)));
    setSelected((s) => (s ? reports.find((r) => r.report_id === s.report_id) || null : s));
  }, [reports]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE, safePage * PAGE);

  const stats = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const senders = {};
    reports.forEach((r) => {
      if (r.sender_id) senders[r.sender_id] = (senders[r.sender_id] || 0) + 1;
    });
    return {
      total: reports.length,
      today: reports.filter((r) => {
        const d = parseAny(r.date_time);
        return d && d >= startOfToday;
      }).length,
      media: reports.filter((r) => normMedia(r.media_files).length).length,
      repeat: Object.values(senders).filter((c) => c > 1).length,
      senders,
    };
  }, [reports]);

  const checkedReports = reports.filter((r) => checked.includes(r.report_id));
  const allPageChecked = pageRows.length > 0 && pageRows.every((r) => checked.includes(r.report_id));

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (lightbox) {
        if (e.key === "Escape") setLightbox(null);
        return;
      }
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (e.key === "Escape" && selected) setSelected(null);
      if (e.key === "/" && !typing && !selected) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, confirm, working, selected]);

  const copyText = (text, label = "Copied") => {
    if (!text) return;
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  /* ----- pieces ----- */

  const Person = ({ name, id, file, role, q: hq }) => {
    const n = personName(name);
    return (
      <div className="flex items-center gap-2.5 min-w-0">
        <Avatar file={file} name={n} size={34} />
        <div className="min-w-0">
          <p className="text-xs text-slate-500">{role}</p>
          {n ? (
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
              <Hi text={n} q={hq} />
            </p>
          ) : (
            <p className="text-sm text-slate-400 italic">User not found</p>
          )}
          {id && (
            <p className="text-[11px] text-slate-500 tabular-nums truncate">
              <Hi text={id} q={hq} />
            </p>
          )}
        </div>
      </div>
    );
  };

  const statCards = [
    { label: "Open reports", value: stats.total, icon: FaFlag, tint: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300", onClick: () => setTypeFilter("all") },
    { label: "Reported today", value: stats.today, icon: FaCalendarDay, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300", onClick: () => setRange("today") },
    { label: "With media", value: stats.media, icon: FaPaperclip, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300", onClick: () => setTypeFilter("media") },
    { label: "Senders reported 2+ times", value: stats.repeat, icon: FaUserSlash, tint: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300", onClick: () => setSortBy("newest") },
  ];

  const confirmCopy = () => {
    if (!confirm) return {};
    const n = confirm.reports.length;
    const r0 = confirm.reports[0];
    const sender = personName(r0?.sender_name) || `user ${r0?.sender_id || ""}`;
    switch (confirm.type) {
      case "block_user":
        return { title: n > 1 ? `Block ${n} senders in their groups?` : `Block ${sender} in ${r0?.grp_name || "this group"}?`, body: "They are blocked in this group only. Their app account and other groups stay as they are.", btn: "Yes, block", color: "bg-amber-600 hover:bg-amber-700" };
      case "remove_member":
        return { title: n > 1 ? `Remove ${n} senders from their groups?` : `Remove ${sender} from ${r0?.grp_name || "this group"}?`, body: "Their membership in this group is deleted. They would need to join again.", btn: "Yes, remove", color: "bg-rose-600 hover:bg-rose-700" };
      case "delete_message":
        return { title: n > 1 ? `Delete ${n} messages?` : "Delete this message?", body: "The message is hidden from the group chat for everyone. The report stays open.", btn: "Yes, delete message", color: "bg-rose-600 hover:bg-rose-700" };
      case "resolve":
        return { title: n > 1 ? `Delete ${n} messages and close reports?` : "Delete message and close report?", body: "The message is hidden from the group chat and the report is removed from this list.", btn: "Yes, do both", color: "bg-rose-600 hover:bg-rose-700" };
      default:
        return { title: n > 1 ? `Delete ${n} reports?` : "Delete this report?", body: "Only the report is removed. The message and the user are not changed.", btn: "Yes, delete report", color: "bg-rose-600 hover:bg-rose-700" };
    }
  };
  const cc = confirmCopy();

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="mr-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Groups</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Group Message Reports</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Messages in group chats that members reported. See the message in its group and take action.</p>
        </div>
        <button type="button" onClick={fetchReports} disabled={loading} className="mr-press inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60">
          <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c, i) => {
          const Icon = c.icon;
          return (
            <button key={c.label} type="button" onClick={c.onClick} className={`${CARD} mr-in mr-press p-4 flex items-center gap-4 text-left`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.tint}`}>
                <Icon />
              </span>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{fmt(c.value)}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className={`${CARD} mr-in mt-4 p-4`} style={{ animationDelay: "240ms" }}>
        <div className="relative">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search message text, report title, group, sender, reporter or any ID"
            className={`${INPUT} w-full h-12 pl-11 pr-12 text-base`}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {query ? (
              <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                <FaTimes size={13} />
              </button>
            ) : (
              <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {[
            { id: "all", label: "All" },
            { id: "text", label: "Text only" },
            { id: "media", label: "With media" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTypeFilter(t.id)}
              className={`mr-press h-8 px-3 rounded-full text-sm font-medium border ${
                typeFilter === t.id ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
              }`}
            >
              {t.label}
            </button>
          ))}
          <span className="w-px h-5 bg-slate-200 dark:bg-slate-600 mx-1" />
          <select value={range} onChange={(e) => setRange(e.target.value)} className={`${INPUT} h-8 px-2`}>
            <option value="all">Any date</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-8 px-2`}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          {(q || typeFilter !== "all" || range !== "all") && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setTypeFilter("all");
                setRange("all");
              }}
              className="text-sm font-medium text-cyan-700 dark:text-cyan-400 hover:underline"
            >
              Clear all
            </button>
          )}
          <label className="ml-auto inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={allPageChecked}
              onChange={() =>
                setChecked((c) =>
                  allPageChecked ? c.filter((id) => !pageRows.some((r) => r.report_id === id)) : Array.from(new Set([...c, ...pageRows.map((r) => r.report_id)]))
                )
              }
              className="w-4 h-4 accent-cyan-600"
            />
            Select page
          </label>
        </div>
      </div>

      {/* Bulk bar */}
      {checked.length > 0 && (
        <div className="mr-fade mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20 px-4 py-2.5 text-sm">
          <span className="font-medium text-cyan-900 dark:text-cyan-100 mr-2">{checked.length} selected</span>
          <button type="button" onClick={() => setConfirm({ type: "delete_report", reports: checkedReports })} className="mr-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white font-medium">
            <FaTrash size={11} /> Delete reports
          </button>
          <button type="button" onClick={() => setConfirm({ type: "resolve", reports: checkedReports })} className="mr-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-rose-300 bg-white dark:bg-secondary-dark-bg text-rose-700 font-medium">
            <FaCheckDouble size={11} /> Delete messages & close
          </button>
          <button type="button" onClick={() => setChecked([])} className="ml-auto text-slate-600 dark:text-slate-300 hover:underline">
            Clear selection
          </button>
        </div>
      )}

      {/* Results */}
      <p className="mt-4 mb-3 text-sm text-slate-600 dark:text-slate-300">
        {q ? (
          <>
            <b>{filtered.length}</b> result{filtered.length === 1 ? "" : "s"} for "<b>{q}</b>"
          </>
        ) : (
          <>
            <b>{filtered.length}</b> report{filtered.length === 1 ? "" : "s"}
          </>
        )}
      </p>

      {loading && !reports.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`${CARD} h-72 animate-pulse`} />
          ))}
        </div>
      ) : pageRows.length === 0 ? (
        <div className={`${CARD} px-5 py-14 text-center`}>
          <FaInbox className="mx-auto text-slate-300" size={34} />
          <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">{q ? `No report found for "${q}"` : "No message reports"}</p>
          <p className="text-sm text-slate-500">{q ? "Try a different word or clear the filters." : "When users report a chat message it will show here."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
          {pageRows.map((r, i) => {
            const media = normMedia(r.media_files);
            const d = parseAny(r.date_time);
            const isChecked = checked.includes(r.report_id);
            const repeat = r.sender_id ? stats.senders[r.sender_id] || 0 : 0;
            return (
              <article
                key={r.report_id}
                onClick={() => setSelected(r)}
                className={`mr-card mr-in rounded-xl border bg-white dark:bg-secondary-dark-bg cursor-pointer overflow-hidden ${
                  isChecked ? "border-cyan-500 ring-2 ring-cyan-500/30" : "border-slate-200 dark:border-slate-700"
                }`}
                style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
              >
                {/* Report note */}
                <div className="flex items-start gap-3 px-4 py-3 bg-rose-50/70 dark:bg-rose-900/15 border-b border-rose-100 dark:border-rose-900/40">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => setChecked((c) => (c.includes(r.report_id) ? c.filter((x) => x !== r.report_id) : [...c, r.report_id]))}
                    className="mt-1 w-4 h-4 accent-cyan-600 cursor-pointer"
                    aria-label={`Select report ${r.report_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FaFlag className="text-rose-500 shrink-0" size={11} />
                      <p className="font-semibold text-slate-900 dark:text-white truncate">
                        <Hi text={r.title || "No title"} q={q} />
                      </p>
                    </div>
                    {r.description && (
                      <p className="text-sm text-slate-600 dark:text-slate-300 truncate">
                        <Hi text={r.description} q={q} />
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 whitespace-nowrap">#{r.report_id}</span>
                </div>

                {/* Chat evidence */}
                <div className="px-4 py-4">
                  <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
                    <span className="truncate font-medium text-slate-700 dark:text-slate-200">{personName(r.sender_name) || "Unknown sender"}</span>
                    <FaArrowRight size={9} />
                    <span className="truncate inline-flex items-center gap-1"><FaUsers size={10} /> {r.grp_name ? <Hi text={r.grp_name} q={q} /> : "Unknown group"}</span>
                    {repeat > 1 && (
                      <span className="ml-auto shrink-0 font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                        {repeat}× reported
                      </span>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <Avatar file={r.sender_profile} name={personName(r.sender_name)} size={30} />
                    <div className="flex-1 min-w-0 rounded-2xl rounded-bl-sm bg-slate-100 dark:bg-slate-700 px-3.5 py-2.5">
                      {r.message_text ? (
                        <p className="mr-clamp3 text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words">
                          <Hi text={r.message_text} q={q} />
                        </p>
                      ) : !media.length ? (
                        <p className="text-sm text-slate-400 italic">{r.msg_id ? "Message text not available" : "This message no longer exists"}</p>
                      ) : null}
                      {media.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5" onClick={(e) => e.stopPropagation()}>
                          {media.slice(0, 3).map((m, mi) =>
                            m.type === "image" || m.type === "video" ? (
                              <SmartMedia key={mi} media={m} onOpen={setLightbox} />
                            ) : (
                              <span key={mi} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-white/80 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                <MediaIcon type={m.type} /> {m.type}
                              </span>
                            )
                          )}
                          {media.length > 3 && <span className="text-xs text-slate-500 self-center">+{media.length - 3}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="mt-1.5 ml-10 text-[11px] text-slate-400">Message #{r.msg_id || "-"}</p>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-slate-100 dark:border-slate-700 text-xs text-slate-500">
                  <span className="truncate">
                    Reported by <b className="text-slate-700 dark:text-slate-200">{personName(r.reporter_name) || "unknown user"}</b>
                  </span>
                  <span className="whitespace-nowrap">
                    {fmtDate(d)}, {fmtTime(d)}
                  </span>
                </div>
                <div className="grid grid-cols-3 border-t border-slate-100 dark:border-slate-700 divide-x divide-slate-100 dark:divide-slate-700" onClick={(e) => e.stopPropagation()}>
                  <button type="button" onClick={() => setConfirm({ type: "delete_message", reports: [r] })} disabled={!r.msg_id} className="disabled:opacity-40 h-10 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 active:bg-rose-100 transition-colors">
                    <FaCommentSlash size={11} /> Delete Msg
                  </button>
                  <button type="button" onClick={() => setConfirm({ type: "block_user", reports: [r] })} disabled={!r.sender_id} className="h-10 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 active:bg-amber-100 transition-colors disabled:opacity-40">
                    <FaBan size={11} /> Block
                  </button>
                  <button type="button" onClick={() => setConfirm({ type: "delete_report", reports: [r] })} className="h-10 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/40 active:bg-slate-100 transition-colors">
                    <FaTrash size={11} /> Delete Report
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {filtered.length > PAGE && (
        <div className="flex items-center justify-center gap-3 mt-5 text-sm">
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="mr-press w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
            <FaChevronLeft size={11} />
          </button>
          <span className="tabular-nums text-slate-600 dark:text-slate-300">
            Page {safePage} of {totalPages}
          </span>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="mr-press w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Next page">
            <FaChevronRight size={11} />
          </button>
        </div>
      )}

      {/* ================= Detail drawer ================= */}
      {selected && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="mr-fade absolute inset-0 bg-slate-900/40" onClick={() => setSelected(null)} />
          <aside className="mr-slide relative w-full max-w-lg h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="min-w-0">
                <p className="text-xs text-slate-500">Report #{selected.report_id}</p>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white truncate">{selected.title || "Report Details"}</h2>
                <p className="text-sm text-slate-500">
                  {fmtDate(parseAny(selected.date_time))}, {fmtTime(parseAny(selected.date_time))}
                </p>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="mr-press w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close">
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Why reported */}
              <section className="rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/40 p-4">
                <p className="flex items-center gap-2 text-xs font-semibold text-rose-700 dark:text-rose-300">
                  <FaFlag size={10} /> Why it was reported
                </p>
                <p className="mt-1 font-semibold text-slate-900 dark:text-white">{selected.title || "No title"}</p>
                <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">{selected.description || "No description"}</p>
              </section>

              {/* Conversation */}
              <section>
                <h3 className="text-xs font-semibold text-slate-500 mb-2">Reported message</h3>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 dark:bg-slate-800/60 text-sm">
                    <span className="font-medium text-slate-800 dark:text-slate-100 truncate">{personName(selected.sender_name) || "Unknown sender"}</span>
                    <FaArrowRight size={10} className="text-slate-400 shrink-0" />
                    <button type="button" onClick={() => selected.group_id && navigate(`/group-chat/${selected.group_id}`)} className="text-slate-600 dark:text-slate-300 truncate inline-flex items-center gap-1 hover:underline">
                      <FaUsers size={10} /> {selected.grp_name || "Unknown group"}
                    </button>
                    <button type="button" onClick={() => copyText(selected.msg_id, "Message ID copied")} className="ml-auto text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 shrink-0">
                      #{selected.msg_id} <FaCopy size={9} />
                    </button>
                  </div>
                  <div className="p-4" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(148,163,184,.22) 1px, transparent 0)", backgroundSize: "14px 14px" }}>
                    <div className="mr-pop flex items-end gap-2">
                      <Avatar file={selected.sender_profile} name={personName(selected.sender_name)} size={32} />
                      <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white dark:bg-slate-700 shadow-sm px-4 py-3 ring-2 ring-rose-300/60">
                        {selected.message_text ? (
                          <p className="text-sm text-slate-900 dark:text-white whitespace-pre-wrap break-words">{selected.message_text}</p>
                        ) : (
                          <p className="text-sm text-slate-400 italic">No text in this message</p>
                        )}
                        {normMedia(selected.media_files).length > 0 && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            {normMedia(selected.media_files).map((m, mi) => (
                              <div key={mi} className={m.type === "audio" || m.type === "file" ? "col-span-2" : ""}>
                                <SmartMedia media={m} size="full" onOpen={setLightbox} />
                              </div>
                            ))}
                          </div>
                        )}
                        {selected.sent_at && (
                          <p className="mt-1.5 text-[10px] text-slate-400 text-right">
                            Sent {fmtDate(parseAny(selected.sent_at))}, {fmtTime(parseAny(selected.sent_at))}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* People */}
              <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { role: "Sender (reported)", name: selected.sender_name, id: selected.sender_id, file: selected.sender_profile },
                  { role: "Group", name: selected.grp_name, id: selected.group_id, file: null, group: true },
                  { role: "Reported by", name: selected.reporter_name, id: selected.reporter_id, file: selected.reporter_profile },
                ].map((p) => (
                  <div key={p.role} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                    <Person name={p.name} id={p.id} file={p.file} role={p.role} />
                    {p.id && (
                      <button type="button" onClick={() => navigate(p.group ? `/group-chat/${p.id}` : `/user-profile/${p.id}`)} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-cyan-700 dark:text-cyan-400 hover:underline">
                        {p.group ? "Open chat" : "Profile"} <FaExternalLinkAlt size={9} />
                      </button>
                    )}
                  </div>
                ))}
              </section>

              {!selected.msg_id && (
                <p className="flex items-start gap-2 rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200 text-sm p-3">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  The reported message no longer exists (it may have been removed), so the group and sender can't be shown. You can only delete this report.
                </p>
              )}
              {selected.msg_id && !selected.sender_id && (
                <p className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-sm p-3">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  The sender's account was not found (it may have been deleted), so blocking is not possible for this report.
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 space-y-2">
              <button type="button" onClick={() => setConfirm({ type: "resolve", reports: [selected] })} disabled={!selected.msg_id} className="mr-press disabled:opacity-40 w-full h-11 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
                <FaCheckDouble size={12} /> Delete message & close report
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setConfirm({ type: "delete_message", reports: [selected] })} disabled={!selected.msg_id} className="mr-press disabled:opacity-40 h-10 rounded-lg border border-rose-200 dark:border-rose-800 bg-white dark:bg-secondary-dark-bg text-rose-600 text-xs font-semibold inline-flex items-center justify-center gap-1.5">
                  <FaCommentSlash size={11} /> Delete Msg
                </button>
                <button type="button" onClick={() => setConfirm({ type: "block_user", reports: [selected] })} disabled={!selected.sender_id} className="mr-press h-10 rounded-lg border border-amber-200 dark:border-amber-800 bg-white dark:bg-secondary-dark-bg text-amber-700 text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40">
                  <FaBan size={11} /> Block in group
                </button>
                <button type="button" onClick={() => setConfirm({ type: "remove_member", reports: [selected] })} disabled={!selected.sender_id || !selected.group_id} className="mr-press h-10 rounded-lg border border-rose-200 dark:border-rose-800 bg-white dark:bg-secondary-dark-bg text-rose-700 text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40">
                  <FaUserSlash size={11} /> Remove from group
                </button>
                <button type="button" onClick={() => setConfirm({ type: "delete_report", reports: [selected] })} className="mr-press h-10 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-slate-700 dark:text-slate-200 text-xs font-semibold inline-flex items-center justify-center gap-1.5">
                  <FaTrash size={11} /> Delete Report
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1300 }}>
          <div className="mr-fade absolute inset-0 bg-black/85" onClick={() => setLightbox(null)} />
          <img src={lightbox} alt="" className="mr-zoom relative max-w-full max-h-[85vh] rounded-lg object-contain" />
          <div className="absolute top-4 right-4 flex gap-2">
            <a href={lightbox} target="_blank" rel="noreferrer" className="mr-press w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center" aria-label="Open original">
              <FaExternalLinkAlt size={13} />
            </a>
            <button type="button" onClick={() => setLightbox(null)} className="mr-press w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center" aria-label="Close">
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {/* Confirm */}
      {confirm && confirm.reports.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="mr-fade absolute inset-0 bg-slate-900/50" onClick={() => !working && setConfirm(null)} />
          <div className="mr-zoom relative w-full max-w-md rounded-xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-11 h-11 rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300 flex items-center justify-center">
              <FaExclamationTriangle size={18} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{cc.title}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{cc.body}</p>
            {confirm.reports.length === 1 && confirm.reports[0].message_text && (
              <p className="mt-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3 text-sm text-slate-700 dark:text-slate-200 mr-clamp3">"{confirm.reports[0].message_text}"</p>
            )}
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="mr-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Cancel
              </button>
              <button type="button" onClick={runConfirm} disabled={working} className={`mr-press flex-1 h-10 rounded-lg text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 ${cc.color}`}>
                {working && <FaSyncAlt className="animate-spin" size={12} />}
                {working ? "Please wait..." : cc.btn}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupMessageReport;